import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, ArrowLeft, Sparkles, CheckCircle2 } from "lucide-react";

/**
 * OnboardingTour — a lightweight custom spotlight tour for /studio.
 * Highlights key UI elements by test-id, walks first-time signups through setup.
 *
 * Props:
 *   steps    – ordered array of { targetTestId | targetSelector, title, body, placement, beforeShow?, tab?, requireVisible? }
 *   onDone   – called when the user finishes or skips
 *   startOpen – when true, the tour renders immediately
 *   testIdPrefix – testid namespace (default: "studio-tour")
 */
export default function OnboardingTour({ steps, onDone, startOpen = true, testIdPrefix = "studio-tour" }) {
  const [open, setOpen] = useState(startOpen);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [placement, setPlacement] = useState("bottom");
  const step = steps[idx];
  const tooltipRef = useRef(null);

  const measure = useCallback(() => {
    if (!step) return;
    // Center card if no target
    if (!step.targetTestId && !step.targetSelector) {
      setRect(null);
      return;
    }
    const sel = step.targetTestId ? `[data-testid="${step.targetTestId}"]` : step.targetSelector;
    const el = document.querySelector(sel);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    // Bring into view smoothly if the target is off-screen
    const viewport = { h: window.innerHeight, w: window.innerWidth };
    const outOfView = r.top < 80 || r.bottom > viewport.h - 80;
    if (outOfView) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Re-measure after the scroll settles
      setTimeout(() => {
        const r2 = el.getBoundingClientRect();
        setRect({ top: r2.top, left: r2.left, width: r2.width, height: r2.height });
      }, 380);
      return;
    }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    setPlacement(step.placement || (r.bottom + 220 > viewport.h ? "top" : "bottom"));
  }, [step]);

  // Whenever the step changes: run its beforeShow (e.g. switch tab), then measure.
  useEffect(() => {
    if (!open || !step) return;
    let cancelled = false;
    const run = async () => {
      if (step.beforeShow) {
        try { await step.beforeShow(); } catch { /* ignore */ }
      }
      if (cancelled) return;
      // wait a beat for the DOM to update
      setTimeout(() => { if (!cancelled) measure(); }, 250);
    };
    run();
    return () => { cancelled = true; };
  }, [idx, open, step, measure]);

  // Re-measure on resize/scroll
  useEffect(() => {
    if (!open) return;
    const onResize = () => measure();
    const onScroll = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, measure]);

  const next = () => {
    if (idx + 1 >= steps.length) {
      finish();
    } else {
      setIdx(idx + 1);
    }
  };
  const back = () => idx > 0 && setIdx(idx - 1);
  const skip = () => finish();
  const finish = () => {
    setOpen(false);
    onDone?.();
  };

  if (!open || !step) return null;

  // Tooltip position
  const tooltipStyle = (() => {
    if (!rect) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    const gap = 16;
    const tw = 380;
    if (placement === "top") {
      return {
        top: Math.max(24, rect.top - gap - 220),
        left: Math.max(16, Math.min(window.innerWidth - tw - 16, rect.left + rect.width / 2 - tw / 2)),
      };
    }
    return {
      top: rect.top + rect.height + gap,
      left: Math.max(16, Math.min(window.innerWidth - tw - 16, rect.left + rect.width / 2 - tw / 2)),
    };
  })();

  const isLast = idx === steps.length - 1;
  const totalSteps = steps.length;

  return (
    <div className="fixed inset-0 z-[1000]" data-testid={testIdPrefix} aria-modal="true" role="dialog">
      {/* SVG mask with a cutout for the spotlight */}
      <svg className="absolute inset-0 w-full h-full pointer-events-auto" style={{ pointerEvents: "auto" }}>
        <defs>
          <mask id={`${testIdPrefix}-spotlight-mask`}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 6}
                y={rect.top - 6}
                width={rect.width + 12}
                height={rect.height + 12}
                rx="16"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(24, 61, 54, 0.72)"
          mask={`url(#${testIdPrefix}-spotlight-mask)`}
          onClick={skip}
        />
        {rect && (
          <rect
            x={rect.left - 6}
            y={rect.top - 6}
            width={rect.width + 12}
            height={rect.height + 12}
            rx="16"
            fill="none"
            stroke="#F4B183"
            strokeWidth="2"
            style={{ filter: "drop-shadow(0 0 12px rgba(244, 177, 131, 0.5))" }}
          />
        )}
      </svg>

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="absolute w-[380px] max-w-[calc(100vw-32px)] bg-[#FAF7F0] rounded-3xl border border-[#E5E0D8] shadow-2xl p-6"
        style={tooltipStyle}
        data-testid={`${testIdPrefix}-card`}
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-2xl bg-[#183D36] text-[#F4B183] flex items-center justify-center">
            {isLast ? <CheckCircle2 size={14} /> : <Sparkles size={14} />}
          </span>
          <span className="font-accent text-xs uppercase tracking-widest text-[#4A4A4A]">
            Step {idx + 1} of {totalSteps}
          </span>
          <button
            onClick={skip}
            aria-label="Skip tour"
            className="ml-auto text-[#4A4A4A] hover:text-[#183D36] p-1"
            data-testid={`${testIdPrefix}-skip`}
          >
            <X size={16} />
          </button>
        </div>
        <h3 className="font-heading text-xl text-[#183D36] mb-2" data-testid={`${testIdPrefix}-title`}>
          {step.title}
        </h3>
        <div className="text-sm text-[#252525] mb-5 leading-relaxed" data-testid={`${testIdPrefix}-body`}>
          {step.body}
        </div>
        <div className="flex items-center gap-2">
          {idx > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={back}
              className="rounded-full border border-[#183D36]/20 hover:bg-white"
              data-testid={`${testIdPrefix}-back`}
            >
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
          )}
          <div className="flex-1 flex items-center gap-1.5 justify-center">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-[#183D36]" : i < idx ? "w-1.5 bg-[#A8BFA3]" : "w-1.5 bg-[#E5E0D8]"
                }`}
              />
            ))}
          </div>
          <Button
            size="sm"
            onClick={next}
            className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]"
            data-testid={`${testIdPrefix}-next`}
          >
            {isLast ? "Got it" : "Next"}
            {!isLast && <ArrowRight size={14} className="ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
